import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Stack,
  InputAdornment,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import DataGrid from './ui/DataTable';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import MicrowaveOutlinedIcon from '@mui/icons-material/MicrowaveOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined';

import StatusDonut from './ui/StatusDonut';
import TrendTile from './ui/TrendTile';
import StatTile from './ui/StatTile';
import FunnelTile from './ui/FunnelTile';

import axiosInstance from './axiosInstance';
import { useTheme, alpha } from '@mui/material/styles';

import { isValidEmail } from "../utils/validation";
import { useSnackbar } from "../providers/SnackbarProvider";
import SectionCard from './ui/SectionCard';
import EmptyState from './ui/EmptyState';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';
import { phaseChartColors, phaseChipColors } from '../utils/phaseColors';
import { sourceOptions, titleOptions, scoreOptions } from '../constants/leadOptions';
import { useBreadcrumbs } from './BreadcrumbBar';
import useIsManager from '../hooks/useIsManager';

const EMPTY_LEAD = {
  salutation: '', title: '', firstName: '', lastName: '', email: '', phone: '',
  mobile: '', website: '', status: 'Open', source: '', score: '', company: '',
  position: '', street: '', city: '', zip_code: '', notes: '',
};

const initials = (fn, ln) => `${fn?.[0] ?? ''}${ln?.[0] ?? ''}`.toUpperCase() || '?';

// Leerzustand bei Filter/Suche ohne Treffer (D4) — Slots erwarten Komponenten
const NoResults = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return isNaN(days) ? null : Math.max(0, days);
};

function LeadList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const isManager = useIsManager();
  // Pipeline-Konfiguration (B3): Phasen/Labels + Stale-Schwellen aus dem Admin
  const { phasesFor, lostFor, statusLabelsFor, staleHintDays, staleWarnDays } = usePipelineConfig();
  const leadPhases = phasesFor('lead');
  const leadLost = lostFor('lead');
  const STATUS_LABELS = statusLabelsFor('lead');

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // ?phase=… erlaubt gefilterte Absprünge (z. B. Trichter-Stufe der Startseite)
  const [searchParams] = useSearchParams();
  const [phaseFilter, setPhaseFilter] = useState(searchParams.get('phase') || 'ALL');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);
  const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false });

  useBreadcrumbs([{ label: 'Leads' }]);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const response = await axiosInstance.get('/leads/');
        setLeads(response.data);
      } catch (error) {
        console.error('Error fetching leads', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);

  // ---------- Kennzahlen ----------
  // Design 3.0: Phasenfarben aus der Akzent-Ramp (Charts) bzw. mit
  // grün/rot-Semantik für Chips — abgeleitet aus der Phasen-Reihenfolge.
  const chartColors = phaseChartColors(theme, leadPhases, leadLost);
  const chipColors = phaseChipColors(theme, leadPhases, leadLost);
  const getStatusColor = (status) => chartColors[status] || theme.palette.tokens.chartNeutral;

  const metrics = useMemo(() => {
    const statusCounts = leads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {});
    const statusDistribution = Object.entries(statusCounts).map(([status, value]) => ({
      id: status,
      label: STATUS_LABELS[status] || status,
      value,
      color: getStatusColor(status),
    }));

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
    // Alle 6 Monate vorbelegen — ein einzelner Datenpunkt erzeugte sonst
    // ein leeres Linien-Path-Attribut (SVG-Konsolenfehler)
    const monthly = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      monthly[d.toLocaleString('default', { month: 'short' })] = 0;
    }
    leads.forEach((lead) => {
      const createdAt = new Date(lead.created_at);
      if (createdAt >= sixMonthsAgo) {
        const key = createdAt.toLocaleString('default', { month: 'short' });
        if (key in monthly) monthly[key] += 1;
      }
    });
    const monthlyTrend = [{ id: 'Neue Leads', data: Object.entries(monthly).map(([x, y]) => ({ x, y })) }];

    const won = leads.filter((l) => l.status === 'Closed Won').length;
    const conversionRate = leads.length ? ((won / leads.length) * 100).toFixed(1) : '0.0';

    // Trichter: wie viele Leads sind MINDESTENS bis Phase i gekommen —
    // kumulativ über die Phasen-Chronik (status_history) + aktuellen Status,
    // damit der Trichter auch bei übersprungenen Phasen monoton bleibt
    const phaseIdx = Object.fromEntries(leadPhases.map((p, i) => [p.key, i]));
    const maxIdx = (l) => {
      const touched = [l.status, ...(Array.isArray(l.status_history) ? l.status_history.map((h) => h.to) : [])];
      return touched.reduce((m, k) => (phaseIdx[k] !== undefined && phaseIdx[k] > m ? phaseIdx[k] : m), -1);
    };
    const reached = leadPhases.map((p, i) => ({
      key: p.key, label: p.label, color: chartColors[p.key],
      count: leads.filter((l) => maxIdx(l) >= i).length,
    }));

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
    const recent = leads.filter((l) => new Date(l.created_at) >= thirtyDaysAgo).length;
    // Vorperiode gleicher Länge — Grundlage des Delta-Chips
    const recentPrev = leads.filter((l) => {
      const d = new Date(l.created_at);
      return d >= sixtyDaysAgo && d < thirtyDaysAgo;
    }).length;

    // Wochenverlauf (12 Wochen) für die Sparkline
    const weekly = Array(12).fill(0);
    const nowMs = Date.now();
    leads.forEach((l) => {
      const age = Math.floor((nowMs - new Date(l.created_at).getTime()) / (7 * 86400000));
      if (age >= 0 && age < 12) weekly[11 - age] += 1;
    });

    // Halbjahres-Summen für die Trend-Kachel
    const sixSum = Object.values(monthly).reduce((a, b) => a + b, 0);
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 11);
    const prevSixSum = leads.filter((l) => {
      const d = new Date(l.created_at);
      return d >= twelveMonthsAgo && d < sixMonthsAgo;
    }).length;

    return { statusDistribution, monthlyTrend, conversionRate, reached, recent, recentPrev, weekly, sixSum, prevSixSum };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  // ---------- Filter ----------
  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (phaseFilter !== 'ALL' && l.status !== phaseFilter) return false;
      if (!q) return true;
      return [l.firstName, l.lastName, l.email, l.company].some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [leads, search, phaseFilter]);

  // ---------- Leerzustände (D4) ----------
  // Suche/Phasen-Filter laufen außerhalb der Grid — bei aktivem Filter ohne
  // Treffer sieht die Grid daher ebenfalls „keine Zeilen". NoRows unterscheidet
  // deshalb selbst zwischen echter Leere und leergefiltertem Bestand.
  const NoRows = useCallback(() => (
    leads.length ? <NoResults /> : (
      <EmptyState
        title="Noch keine Leads."
        hint="Der erste wartet schon irgendwo da draußen."
        actionLabel="Neuen Lead anlegen"
        onAction={() => setCreateDialogOpen(true)}
      />
    )
  ), [leads.length]);

  // ---------- Zellen ----------
  const renderLeadCell = (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
      <Avatar sx={{ width: 32, height: 32, fontSize: '0.78rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark', flex: 'none' }}>
        {initials(params.row.firstName, params.row.lastName)}
      </Avatar>
      <Box sx={{ minWidth: 0, lineHeight: 1.3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {params.row.firstName} {params.row.lastName}
          </Typography>
          {params.row.is_converted && (
            <Tooltip title="In Kontakt konvertiert">
              <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {params.row.email || '—'}
        </Typography>
      </Box>
    </Box>
  );

  const renderPhaseCell = (params) => {
    const color = chipColors[params.value] || theme.palette.tokens.chartNeutral;
    return (
      <Chip size="small" label={STATUS_LABELS[params.value] || params.value}
        sx={{ bgcolor: alpha(color, 0.13), color, fontWeight: 600 }} />
    );
  };

  const renderScoreCell = (params) => {
    const map = {
      Cold: { color: theme.palette.leadScore.cold, label: 'Kalt', icon: <AcUnitOutlinedIcon /> },
      Warm: { color: theme.palette.leadScore.warm, label: 'Warm', icon: <MicrowaveOutlinedIcon /> },
      Hot: { color: theme.palette.leadScore.hot, label: 'Heiß', icon: <LocalFireDepartmentOutlinedIcon /> },
    };
    if (!params.value) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const s = map[params.value] || { color: theme.palette.leadScore.unknown, label: params.value, icon: <QuestionMarkOutlinedIcon /> };
    return (
      <Chip size="small" icon={React.cloneElement(s.icon, { style: { color: s.color, fontSize: '0.95rem' } })}
        label={s.label} sx={{ bgcolor: alpha(s.color, 0.13), color: s.color, fontWeight: 600 }} />
    );
  };

  // „In Phase seit" — nur für laufende Leads interessant; färbt ab den
  // Stale-Schwellen (dieselben wie die Inaktivitäts-Warnung der Detailseite).
  const renderInPhaseCell = (params) => {
    const { row } = params;
    if (row.is_converted || row.status === leadLost?.key || row.status === leadPhases[leadPhases.length - 1]?.key) {
      return <Typography variant="caption" color="text.disabled">—</Typography>;
    }
    const days = daysSince(row.phase_changed_at);
    if (days === null) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const color = days >= staleWarnDays ? 'error.main' : days >= staleHintDays ? 'warning.main' : 'text.secondary';
    const weight = days >= staleHintDays ? 700 : 400;
    return (
      <Typography variant="body2" sx={{ color, fontWeight: weight, fontVariantNumeric: 'tabular-nums' }}>
        {days === 0 ? 'heute' : `${days} ${days === 1 ? 'Tag' : 'Tage'}`}
      </Typography>
    );
  };

  const renderChecklistCell = (params) => {
    const { row } = params;
    const phase = leadPhases.find((p) => p.key === row.status);
    if (!phase || !phase.checklist.length) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const done = ((row.checklist_state && row.checklist_state[phase.key]) || []).length;
    const total = phase.checklist.length;
    const complete = done >= total;
    return (
      <Typography variant="body2" sx={{
        color: complete ? 'success.main' : 'text.secondary',
        fontWeight: complete ? 700 : 400, fontVariantNumeric: 'tabular-nums',
      }}>
        {done}/{total}
      </Typography>
    );
  };

  const leadColumns = [
    {
      field: 'name', headerName: 'Lead', minWidth: 230, flex: 1.6,
      valueGetter: (value, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim(),
      renderCell: renderLeadCell,
    },
    {
      field: 'company', headerName: 'Unternehmen', minWidth: 160, flex: 1.1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', minWidth: 0, lineHeight: 1.3 }}>
          <Typography variant="body2" noWrap>{params.value || '—'}</Typography>
          {params.row.city && <Typography variant="caption" color="text.secondary" noWrap>{params.row.city}</Typography>}
        </Box>
      ),
    },
    { field: 'status', headerName: 'Phase', minWidth: 130, flex: 0.8, renderCell: renderPhaseCell },
    { field: 'score', headerName: 'Score', minWidth: 105, flex: 0.6, renderCell: renderScoreCell },
    {
      field: 'phase_changed_at', headerName: 'In Phase seit', minWidth: 110, flex: 0.7,
      valueGetter: (value) => daysSince(value) ?? -1,
      renderCell: renderInPhaseCell,
    },
    {
      field: 'checklist', headerName: 'Checkliste', minWidth: 95, flex: 0.55, sortable: false,
      renderCell: renderChecklistCell,
    },
    {
      field: 'source', headerName: 'Herkunft', minWidth: 110, flex: 0.7,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="caption" color="text.disabled">—</Typography>;
        const option = sourceOptions.find((o) => o.value === params.value);
        return <Typography variant="body2" color="text.secondary">{option ? option.label : params.value}</Typography>;
      },
    },
    // Löschen ist serverseitig Managern vorbehalten (Security S3) —
    // Spalte daher nur für Manager einblenden.
    ...(isManager ? [{
      field: 'actions', headerName: '', width: 56, sortable: false, disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Löschen">
            <IconButton
              variant="action-delete" size="small"
              className="row-delete"
              sx={{ opacity: 0.25, transition: 'opacity .15s' }}
              onClick={(e) => { e.stopPropagation(); handleDeleteLead(params.row.id); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }] : []),
  ];

  // ---------- Anlegen / Löschen ----------
  const errors = {
    firstName: !newLead.firstName ? 'Vorname ist erforderlich' : null,
    lastName: !newLead.lastName ? 'Nachname ist erforderlich' : null,
    email: !newLead.email ? 'E-Mail ist erforderlich' : (!isValidEmail(newLead.email) ? 'Ungültige E-Mail-Adresse' : null),
  };

  const handleBlur = (e) => setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  const handleInputChange = (e) => setNewLead((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleClose = () => {
    setCreateDialogOpen(false);
    setTouched({ firstName: false, lastName: false, email: false });
    setNewLead(EMPTY_LEAD);
  };

  const handleCreateLead = async () => {
    if (errors.firstName || errors.lastName || errors.email) {
      showSnackbar('Bitte Pflichtfelder prüfen', 'error');
      return;
    }
    try {
      const response = await axiosInstance.post('/leads/', newLead);
      setLeads([response.data, ...leads]);
      handleClose();
      showSnackbar('Lead erfolgreich erstellt', 'success');
    } catch (error) {
      console.error('Error creating lead:', error);
      showSnackbar('Fehler beim Erstellen', 'error');
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Möchten Sie diesen Lead wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/leads/${id}/`);
      setLeads((prev) => prev.filter((c) => c.id !== id));
      showSnackbar('Lead erfolgreich gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting lead:', error);
      showSnackbar('Fehler beim Löschen des Leads', 'error');
    }
  };

  // ---------- Kacheln ----------
  const tile = (children) => (
    <SectionCard sx={{ height: 180, display: 'flex', flexDirection: 'column' }}>{children}</SectionCard>
  );

  const phaseFilters = [{ key: 'ALL', label: 'Alle' }, ...leadPhases.map((p) => ({ key: p.key, label: p.label })), ...(leadLost ? [{ key: leadLost.key, label: leadLost.label }] : [])];

  return (
    <>
      <Grid container spacing={2.5}>
        {/* Kopf */}
        <Grid size={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
            <Box>
              <Typography variant="h4" component="h1">Leads</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {filteredLeads.length} {filteredLeads.length === 1 ? 'Lead' : 'Leads'}
                {(search || phaseFilter !== 'ALL') ? ` (von ${leads.length})` : ''}
              </Typography>
            </Box>
            <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }}>
              Neuer Lead
            </Button>
          </Stack>
        </Grid>

        {/* Kennzahlen */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Statusverteilung</Typography>
              {/* Donut 2.0: Zentrum-KPI + Wertelegende; Klick filtert die Liste */}
              <StatusDonut
                data={metrics.statusDistribution}
                centerLabel="Leads"
                onSliceClick={(id) => setPhaseFilter(id)}
              />
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <TrendTile
                label="Neue Leads · 6 Monate"
                points={metrics.monthlyTrend[0].data}
                value={metrics.sixSum}
                delta={metrics.sixSum - metrics.prevSixSum}
                deltaLabel="vs. Vorhalbjahr"
              />
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <FunnelTile
                label="Konversion"
                value={`${metrics.conversionRate}%`}
                steps={metrics.reached}
                onStepClick={(key) => setPhaseFilter(key)}
              />
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <StatTile
                label="Zugang · 30 Tage"
                value={metrics.recent}
                sub="neue Leads"
                delta={metrics.recent - metrics.recentPrev}
                deltaLabel="vs. Vorperiode"
                spark={metrics.weekly}
              />
            </>
          )}
        </Grid>

        {/* Suche + stille Phasen-Filter */}
        <Grid size={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              size="small" placeholder="Leads durchsuchen …" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260, maxWidth: 400, bgcolor: 'background.paper' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment> }}
            />
            <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
              {phaseFilters.map((f) => {
                const active = phaseFilter === f.key;
                return (
                  <Box
                    key={f.key} role="button" tabIndex={0}
                    onClick={() => setPhaseFilter(f.key)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPhaseFilter(f.key); } }}
                    sx={{
                      fontSize: '0.8rem', fontWeight: 600, px: active ? 1.25 : 0.75, py: 0.3, borderRadius: 999,
                      cursor: 'pointer', userSelect: 'none',
                      color: active ? 'primary.main' : 'text.disabled',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      '&:hover': { color: active ? 'primary.main' : 'text.secondary' },
                      '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
                    }}
                  >
                    {f.label}
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </Grid>

        {/* Tabelle als Fläche */}
        <Grid size={12}>
          <SectionCard sx={{ p: 0, overflow: 'hidden' }}>
            <DataGrid
              rows={filteredLeads}
              columns={leadColumns}
              loading={loading}
              getRowId={(row) => row.id}
              onRowClick={(params) => navigate(`/leads/${params.row.id}`)}
              disableRowSelectionOnClick
              autoHeight
              rowHeight={56}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRows, noResultsOverlay: NoResults }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-row:hover .row-delete': { opacity: 1 },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
              }}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Lead-anlegen-Dialog */}
      <Dialog open={createDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Neuen Lead erstellen</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" color="text.secondary">Stammdaten</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField select name="salutation" label="Anrede" value={newLead.salutation} onChange={handleInputChange} fullWidth>
                  <MenuItem value="">Keine Auswahl</MenuItem>
                  <MenuItem value="Herr">Herr</MenuItem>
                  <MenuItem value="Frau">Frau</MenuItem>
                  <MenuItem value="Divers">Divers</MenuItem>
                </TextField>
                <TextField select name="title" label="Titel" value={newLead.title ?? ''} onChange={handleInputChange} fullWidth>
                  {titleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField name="firstName" label="Vorname" value={newLead.firstName} onChange={handleInputChange} onBlur={handleBlur}
                  fullWidth required error={touched.firstName && !!errors.firstName} helperText={touched.firstName && errors.firstName} />
                <TextField name="lastName" label="Nachname" value={newLead.lastName} onChange={handleInputChange} onBlur={handleBlur}
                  fullWidth required error={touched.lastName && !!errors.lastName} helperText={touched.lastName && errors.lastName} />
                <TextField name="company" label="Unternehmen" value={newLead.company} onChange={handleInputChange} fullWidth />
                <TextField name="position" label="Position" value={newLead.position} onChange={handleInputChange} fullWidth />
              </Box>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Kontaktdaten</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField name="email" label="E-Mail" type="email" value={newLead.email} onChange={handleInputChange} onBlur={handleBlur}
                  fullWidth required error={touched.email && !!errors.email} helperText={touched.email && errors.email} />
                <TextField name="phone" label="Telefon (Festnetz)" value={newLead.phone} onChange={handleInputChange} fullWidth />
                <TextField name="mobile" label="Telefon (Mobil)" value={newLead.mobile} onChange={handleInputChange} fullWidth />
                <TextField name="website" label="Webseite" value={newLead.website} onChange={handleInputChange} fullWidth />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 3 } }}>
              <Typography variant="overline" color="text.secondary">Vertrieb</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField select name="status" label="Phase" value={newLead.status} onChange={handleInputChange} fullWidth>
                  {leadPhases.map((p) => (
                    <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                  ))}
                  {leadLost && <MenuItem value={leadLost.key}>{leadLost.label}</MenuItem>}
                </TextField>
                <TextField select name="source" label="Herkunft" value={newLead.source} onChange={handleInputChange} fullWidth>
                  <MenuItem value="">Keine Auswahl</MenuItem>
                  {sourceOptions.filter((o) => o.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField select name="score" label="Score" value={newLead.score} onChange={handleInputChange} fullWidth>
                  <MenuItem value="">Keine Auswahl</MenuItem>
                  {scoreOptions.filter((o) => o.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Adresse</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField name="street" label="Straße" value={newLead.street} onChange={handleInputChange} fullWidth />
                <TextField name="zip_code" label="PLZ" value={newLead.zip_code} onChange={handleInputChange} fullWidth />
                <TextField name="city" label="Stadt" value={newLead.city} onChange={handleInputChange} fullWidth />
              </Box>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Notizen</Typography>
              <Box sx={{ mt: 1 }}>
                <TextField name="notes" label="Notizen" value={newLead.notes} onChange={handleInputChange} multiline rows={4} fullWidth />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button onClick={handleCreateLead} variant="contained" disabled={!newLead.firstName || !newLead.lastName || !newLead.email}>
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LeadList;
